import React from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronRight, User, Wrench, ShieldCheck, Zap, Wind, Car } from 'lucide-react';
import { Service, TeamMember, Testimonial, cn } from '@/lib/index';
const iconMap: Record<string, React.ReactNode> = {
  Wrench: <Wrench className="w-6 h-6" />,
  ShieldCheck: <ShieldCheck className="w-6 h-6" />,
  Zap: <Zap className="w-6 h-6" />,
  Wind: <Wind className="w-6 h-6" />,
  Car: <Car className="w-6 h-6" />
};
interface ServiceCardProps {
  service: Service;
}
export function ServiceCard({
  service
}: ServiceCardProps) {
  return <motion.div whileHover={{
    y: -5
  }} className="group relative overflow-hidden rounded-xl border border-border bg-card p-6 shadow-sm transition-all hover:border-primary/20 hover:shadow-lg">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
        {iconMap[service.iconName] || <Wrench className="w-6 h-6" />}
      </div>
      <h3 className="mb-2 text-xl font-bold text-foreground">{service.title}</h3>
      <p className="mb-4 text-sm text-muted-foreground line-clamp-2">
        {service.description}
      </p>
      <div className="mb-6 space-y-2">
        {service.benefits.slice(0, 3).map((benefit, index) => <div key={index} className="flex items-center text-xs text-muted-foreground">
            <div className="mr-2 h-1 w-1 rounded-full bg-primary" />
            {benefit}
          </div>)}
      </div>
      <div className="flex items-center justify-between">
        {service.priceStartingFrom && <span className="text-xs font-mono text-muted-foreground">
            Ab <span className="text-sm font-bold text-foreground">{service.priceStartingFrom}</span>
          </span>}
        <button className="inline-flex items-center text-sm font-semibold text-primary group-hover:underline">
          Details
          <ChevronRight className="ml-1 h-4 w-4" />
        </button>
      </div>
    </motion.div>;
}
interface TeamCardProps {
  member: TeamMember;
}
export function TeamCard({
  member
}: TeamCardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className="flex flex-col items-center text-center">
      <div className="relative mb-4 h-48 w-48 overflow-hidden rounded-full border-4 border-muted ring-2 ring-primary/20">
        <img src={member.image} alt={member.name} className="h-full w-full object-cover transition-transform duration-500 hover:scale-110" />
      </div>
      <h3 className="text-xl font-bold text-foreground">{member.name}</h3>
      <p className="text-sm font-medium text-primary uppercase tracking-wider mb-2">{member.role}</p>
      <div className="inline-flex items-center rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground mb-3">
        <Wrench className="mr-1 h-3 w-3" />
        {member.specialization}
      </div>
      <p className="max-w-xs text-sm text-muted-foreground">
        {member.bio}
      </p>
    </motion.div>;
}
interface TestimonialCardProps {
  testimonial: Testimonial;
}
export function TestimonialCard({
  testimonial
}: TestimonialCardProps) {
  return <div className="flex h-full flex-col rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="mb-4 flex gap-1">
        {[...Array(5)].map((_, i) => <Star key={i} className={cn("h-4 w-4", i < testimonial.rating ? "fill-chart-4 text-chart-4" : "text-muted")} />)}
      </div>
      <blockquote className="flex-1 text-sm italic text-muted-foreground mb-6">
        "{testimonial.text}"
      </blockquote>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-foreground">
          <User className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm font-bold text-foreground">{testimonial.name}</p>
          <p className="text-xs text-muted-foreground">
            {testimonial.vehicleModel} • {testimonial.location}
          </p>
        </div>
      </div>
    </div>;
}
interface BeforeAfterCardProps {
  title: string;
  beforeImage: string;
  afterImage: string;
  description: string;
}
export function BeforeAfterCard({
  title,
  beforeImage,
  afterImage,
  description
}: BeforeAfterCardProps) {
  return <div className="group overflow-hidden rounded-2xl border border-border bg-card shadow-md transition-all hover:shadow-xl">
      <div className="grid grid-cols-1 md:grid-cols-2">
        <div className="relative h-64 overflow-hidden">
          <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2023093696059461632_photo_2026-02-15_18-30-53.jpg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Vorher" className="w-full h-[256px] object-cover" />
          <div className="absolute top-4 left-4 rounded bg-destructive/80 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur-sm">
            Vorher
          </div>
        </div>
        <div className="relative h-64 overflow-hidden border-t md:border-t-0 md:border-l border-border">
          <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2023093765966053376_photo_2026-02-15_18-31-15.jpg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Nachher" className="w-full transition-transform duration-700 group-hover:scale-105 h-[296.3493957519531px] object-cover" />
          <div className="absolute top-4 left-4 rounded bg-primary/80 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur-sm">
            Nachher
          </div>
        </div>
      </div>
      
    </div>;
}